import * as T from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { createSceneKit } from '@london-life/game/scene/kit';
import type { createProps } from '@london-life/game/city/props';
import { AIRPORT_SITES, LAND, siteAt, type AirportSite } from '@london-life/game/city/layout';
import { buildJet, JET_GROUND, type Jet, type Livery } from '@london-life/game/flight/jet';
import type { MapPoint } from '@london-life/game/city/types';

// Heathrow and Gatwick on their slabs joined to the map's edges, drawn as Lagos Life's airport is: runways with
// their markings, numbers and lights, taxiways with their yellow lines, the apron, jets of the game's airline
// parked at the gates with jet bridges to the terminal (its name on the roof), the jet centre with private jets
// and a helipad, the control tower, the car parks full of cars, the forecourt, trees and the road in.

type Kit = ReturnType<typeof createSceneKit>;
type Props = ReturnType<typeof createProps>;

// an airliner on the map: about 10 map units long (bigger than true to the map, so it reads as Lagos Life's do)
export const MAP_JET_SCALE = 0.27;
const PRIVATE_JET_SCALE = 0.13;
// a private jet's own paint (the jet centre's, not the airline's)
const PRIVATE_LIVERY: Livery = { body: '#fbfbf8', tail: '#1d2433', stripe: '#c9a227', name: '', code: '' };
const CAR_COLOURS = ['#1f242b', '#c9d1d9', '#a63d2f', '#2f5d8a', '#e9e4da', '#3a3f45', '#7d8b6a', '#e9b526'];

// a jet standing on its wheels: the holder sits on the ground, the jet inside lifted onto its tyres
export function jetOnGround(jet: Jet, scale: number) {
  const holder = new T.Group();
  jet.group.scale.setScalar(scale);
  jet.group.position.y = JET_GROUND * scale + 0.2;
  holder.add(jet.group);
  return holder;
}

export function buildAirports(kit: Kit, props: Props, group: T.Group) {
  const { box, tree, road, car } = props;
  // the gates and stands where jets park; filled (and refilled) in the airline's livery by `setLivery`
  const stands: { holder: T.Object3D; scale: number; airline: boolean }[] = [];
  // roads (journeys follow them)
  const streets: [MapPoint, MapPoint][] = [];

  // the runways' lights: hundreds of them, so drawn together, one batch per colour
  const glowing = new Map<string, [number, number, number, number, number, number][]>();
  const light = (w: number, h: number, d: number, color: string, x: number, y: number, z: number) => {
    if (!glowing.has(color)) glowing.set(color, []);
    glowing.get(color)!.push([w, h, d, x, y, z]);
  };
  const flat = (w: number, d: number, color: string, x: number, z: number, y = 0.2, h = 0.13, turn = 0) =>
    box(w, h, d, color, x, y, z, turn);
  const along = (a: MapPoint, b: MapPoint) => ({
    middle: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as MapPoint,
    length: Math.hypot(b[0] - a[0], b[1] - a[1]),
    turn: -Math.atan2(b[1] - a[1], b[0] - a[0]),
  });

  const draw = (site: AirportSite) => {
    const at = (dx: number, dz: number) => siteAt(site, [dx, dz]);
    // ---- the slab, the same as the city's, and its grass
    box(site.width + 0.6, 1.1, site.depth + 0.6, LAND.edge, site.x, -0.5, site.z);
    box(site.width, 0.12, site.depth, '#9fc883', site.x, 0.06, site.z);
    // the airfield's mown grass between the runways, a little lighter in stripes
    for (let dz = -site.depth / 2 + 8; dz < site.depth / 2 - 4; dz += 16)
      flat(site.width - 8, 6, '#a8cf8c', site.x, site.z + dz, 0.13, 0.02);

    // ---- the runways: asphalt with pale shoulders, edge lines, the dashed centre line, the threshold's piano
    // keys, the touchdown bars and the aiming points, the numbers at each end, edge lights and approach lights
    for (const runway of site.runways) {
      const a = at(...runway.from),
        b = at(...runway.to);
      const { middle, length } = along(a, b);
      const w = runway.width;
      const [cx, cz] = middle;
      flat(length + 6, w + 2.2, '#8d949a', cx, cz, 0.19, 0.12);
      flat(length, w, '#3c434b', cx, cz, 0.21, 0.12);
      const line = '#f4f4ea';
      for (const side of [-1, 1]) flat(length - 2, 0.16, line, cx, cz + side * (w / 2 - 0.35), 0.28, 0.02);
      for (let d = -length / 2 + 14; d < length / 2 - 13; d += 6) flat(3, 0.22, line, cx + d, cz, 0.28, 0.02);
      for (const end of [-1, 1]) {
        const ex = cx + (end * length) / 2;
        // the piano keys across the end
        for (let k = -w / 2 + 0.7; k < w / 2 - 0.5; k += 0.75)
          flat(3.2, 0.36, line, ex - end * 2.4, cz + k, 0.28, 0.02);
        // the aiming point's two big bars, and touchdown bars
        for (const side of [-1, 1]) {
          flat(5, 0.9, line, ex - end * 16, cz + side * 1.4, 0.28, 0.02);
          for (const d of [24, 30]) flat(2.4, 0.35, line, ex - end * d, cz + side * 1.6, 0.28, 0.02);
        }
        // the number, read by a pilot landing on it
        const number = kit.label(runway.ends[end === -1 ? 0 : 1]!, ex - end * 8.5, cz, group, 6, line);
        number.rotation.y = end === -1 ? Math.PI / 2 : -Math.PI / 2;
        // green threshold lights, then the approach lights out over the grass
        for (let k = -w / 2; k <= w / 2; k += 1.2) light(0.25, 0.2, 0.25, '#43e06b', ex + end * 0.4, 0.34, cz + k);
        for (let d = 3; d <= 13; d += 2.5) {
          flat(0.25, 0.25, '#ffffff', ex + end * d, cz, 0.24, 0.1);
          light(0.3, 0.2, 2.4 - d * 0.08, '#fff6d8', ex + end * d, 0.36, cz);
        }
      }
      // the edge lights
      for (let d = -length / 2 + 4; d < length / 2 - 2; d += 9)
        for (const side of [-1, 1]) light(0.22, 0.22, 0.22, '#fff3c4', cx + d, 0.36, cz + side * (w / 2 + 0.3));
    }

    // ---- the taxiways, with their yellow centre lines and blue edge lights
    for (const [from, to] of site.taxiways) {
      const a = at(...from),
        b = at(...to);
      const { middle, length, turn } = along(a, b);
      flat(length + 3, 3.2, '#59626b', middle[0], middle[1], 0.2, 0.12, turn);
      flat(length + 2, 0.14, '#e8c547', middle[0], middle[1], 0.27, 0.02, turn);
    }

    // ---- the apron, the stands' yellow lead-in lines, and the jets at the gates with their jet bridges
    const apron = site.apron;
    flat(apron.w, apron.d, '#c3c7cb', site.x + apron.x, site.z + apron.z, 0.2, 0.13);
    for (const gate of site.gates) {
      const [jx, jz] = at(...gate.at);
      const nose: MapPoint = [jx + Math.cos(gate.turn) * 6, jz - Math.sin(gate.turn) * 6];
      const tail: MapPoint = [jx - Math.cos(gate.turn) * 9, jz + Math.sin(gate.turn) * 9];
      const lead = along(tail, nose);
      flat(lead.length, 0.16, '#e8c547', lead.middle[0], lead.middle[1], 0.28, 0.02, lead.turn);
      const holder = new T.Object3D();
      holder.position.set(jx, 0, jz);
      holder.rotation.y = gate.turn;
      group.add(holder);
      stands.push({ holder, scale: MAP_JET_SCALE, airline: true });
      // the jet bridge: from the terminal to the front door, on its legs
      const door: MapPoint = [
        jx + Math.cos(gate.turn) * 3.6 - Math.sin(gate.turn) * 0.9,
        jz - Math.sin(gate.turn) * 3.6 - Math.cos(gate.turn) * 0.9,
      ];
      const end = at(...gate.bridge);
      const bridge = along(end, door);
      box(bridge.length, 0.9, 0.9, '#aeb6bd', bridge.middle[0], 1.45, bridge.middle[1], bridge.turn);
      box(1.3, 1.1, 1.3, '#8f989f', door[0], 1.45, door[1]);
      box(0.25, 1.2, 0.25, '#5d666e', bridge.middle[0], 0.7, bridge.middle[1]);
      // a tug and a baggage cart beside the jet
      const side: MapPoint = [jx + Math.sin(gate.turn) * 3.2, jz + Math.cos(gate.turn) * 3.2];
      box(1.2, 0.6, 0.8, '#f6b83d', side[0], 0.55, side[1], gate.turn);
      box(
        1.5,
        0.5,
        0.9,
        '#7d8a96',
        side[0] - Math.cos(gate.turn) * 2,
        0.5,
        side[1] + Math.sin(gate.turn) * 2,
        gate.turn,
      );
    }

    // ---- the terminal: a long glass hall under a pale roof, its name on top
    const terminal = site.terminal;
    const [tx, tz] = at(terminal.x, terminal.z);
    box(terminal.w, terminal.h, terminal.d, '#e3e8ec', tx, terminal.h / 2 + 0.25, tz);
    box(terminal.w + 0.12, terminal.h * 0.45, terminal.d + 0.12, '#7fb3cf', tx, terminal.h * 0.55 + 0.25, tz);
    box(terminal.w + 1.2, 0.45, terminal.d + 1.2, '#b8c2ca', tx, terminal.h + 0.45, tz);
    const long = terminal.d > terminal.w;
    const name = kit.label(
      site.name.replace(' AIRPORT', ''),
      tx,
      tz,
      group,
      Math.min(Math.max(terminal.w, terminal.d) * 0.8, 34),
      '#2b3542',
    );
    name.position.y = terminal.h + 0.95;
    if (long) name.rotation.y = Math.PI / 2;
    // roof lights along the hall
    for (let k = -0.38; k <= 0.38; k += 0.19) {
      const lx = long ? tx : tx + k * terminal.w,
        lz = long ? tz + k * terminal.d : tz;
      box(long ? terminal.w * 0.5 : 2.4, 0.2, long ? 2.4 : terminal.d * 0.5, '#d9eef7', lx, terminal.h + 0.7, lz);
    }

    // ---- the jet centre: its own apron, private jets, a hangar and the helipad with a helicopter
    const centre = site.jetCentre;
    const [ccx, ccz] = at(centre.x, centre.z);
    flat(centre.w, centre.d, '#cfd3d6', ccx, ccz, 0.2, 0.13);
    box(centre.w * 0.4, 3.2, 7, '#dfe3e6', ccx + centre.w * 0.22, 1.85, ccz - centre.d / 2 + 4);
    box(centre.w * 0.4 + 0.4, 0.4, 7.4, '#4c1d95', ccx + centre.w * 0.22, 3.6, ccz - centre.d / 2 + 4);
    for (const [k, turn] of [
      [-0.32, -0.6],
      [-0.05, -0.6],
      [0.22, -0.6],
    ] as const) {
      const holder = new T.Object3D();
      holder.position.set(ccx + k * centre.w, 0, ccz + centre.d * 0.18);
      holder.rotation.y = turn;
      group.add(holder);
      stands.push({ holder, scale: PRIVATE_JET_SCALE, airline: false });
    }
    const [hx, hz] = at(...site.helipad);
    kit.cylinder(3.1, 0.12, '#e8c547', hx, 0.22, hz, group, 3.1, 24);
    kit.cylinder(2.7, 0.14, '#4a525b', hx, 0.24, hz, group, 2.7, 24);
    flat(0.35, 2.2, '#f4f4ea', hx - 0.6, hz, 0.32, 0.02);
    flat(0.35, 2.2, '#f4f4ea', hx + 0.6, hz, 0.32, 0.02);
    flat(1.2, 0.35, '#f4f4ea', hx, hz, 0.32, 0.02);
    // the helicopter: the cabin, the tail boom, the rotor
    kit.sphere(0.9, '#1d2433', hx + 3.6, 1.0, hz + 1.5, group).scale.set(1.4, 0.9, 0.9);
    box(2.4, 0.25, 0.25, '#1d2433', hx + 5.2, 1.2, hz + 1.5);
    box(0.2, 0.8, 0.12, '#c9a227', hx + 6.3, 1.5, hz + 1.5);
    box(4.6, 0.06, 0.3, '#3a3f45', hx + 3.6, 1.95, hz + 1.5, 0.5);
    box(0.08, 0.5, 0.08, '#3a3f45', hx + 3.6, 1.7, hz + 1.5);

    // ---- the control tower
    const [cx, cz] = at(...site.tower);
    kit.cylinder(1.0, 14, '#e7eaed', cx, 7.2, cz, group, 0.8, 12);
    kit.cylinder(2.3, 0.5, '#c4ccd3', cx, 14.3, cz, group, 2.3, 12);
    kit.cylinder(2.2, 1.8, '#5d9cc0', cx, 15.4, cz, group, 2.6, 12);
    kit.cylinder(2.7, 0.4, '#e7eaed', cx, 16.5, cz, group, 2.7, 12);
    box(0.12, 2.6, 0.12, '#5b6670', cx, 18, cz);
    kit.glow(0.2, '#ff3b3b', cx, 19.4, cz, group);
    box(7, 3, 6, '#e3e8ec', cx + 4, 1.75, cz + 2);

    // ---- the car parks: bays painted on the tarmac, cars in most of them, lamps
    site.carParks.forEach((park, parkIndex) => {
      const [px, pz] = at(park.x, park.z);
      flat(park.w, park.d, '#6b737c', px, pz, 0.2, 0.13);
      let n = parkIndex * 3;
      for (let row = -park.d / 2 + 3; row < park.d / 2 - 2; row += 5) {
        flat(park.w - 2, 0.12, '#e8ecef', px, pz + row + 1.8, 0.28, 0.02);
        for (let column = -park.w / 2 + 2; column < park.w / 2 - 1.5; column += 3.2) {
          n += 1;
          // a few empty bays
          if (n % 5 === 0) continue;
          car(px + column + 0.8, pz + row, Math.PI / 2 + (n % 2) * Math.PI, CAR_COLOURS[n % CAR_COLOURS.length]!);
        }
      }
    });

    // ---- the forecourt: the drop-off road along the terminal, a fountain, trees, the road in
    const front = site.forecourt;
    const [fx, fz] = at(front.x, front.z);
    flat(front.w, front.d, '#d8d2c2', fx, fz, 0.19, 0.12);
    const wide = front.w > front.d;
    road(fx, fz, (wide ? front.w : front.d) - 2, wide ? 0 : Math.PI / 2, 4);
    const fountain: MapPoint = wide ? [fx, fz - front.d / 2 - 3] : [fx + front.w / 2 + 3, fz];
    kit.cylinder(1.8, 0.5, '#c9ccd0', fountain[0], 0.45, fountain[1], group, 1.8, 16);
    kit.cylinder(1.5, 0.12, '#6cc3e0', fountain[0], 0.72, fountain[1], group, 1.5, 16);
    kit.cylinder(0.25, 1.2, '#dfe8ee', fountain[0], 1.2, fountain[1], group, 0.1, 8);
    for (let k = -0.4; k <= 0.41; k += 0.2) {
      const [x, z] = wide ? [fx + k * front.w, fz - front.d / 2 - 1.2] : [fx + front.w / 2 + 1.2, fz + k * front.d];
      tree(x, z, 0.7, '#4f8f48');
    }
    const roadPoints = site.road.map(([dx, dz]) => at(dx, dz));
    const path = [...roadPoints, ...site.joins];
    for (let i = 1; i < path.length; i += 1) {
      const a = path[i - 1]!,
        b = path[i]!;
      const leg = along(a, b);
      // on to the city's streets (the city keeps its buildings off the way: scene.ts)
      road(leg.middle[0], leg.middle[1], leg.length, leg.turn, 4.2);
      streets.push([a, b]);
    }
    // the airport's name on the grass
    const [lx, lz] = at(...site.label);
    kit.label(site.name, lx, lz, group, 36, '#4f6b45');
    // trees along the slab's landside edge
    for (let k = 0; k < 14; k += 1) {
      const t = (k + 0.5) / 14;
      const [x, z] =
        site.id === 'heathrow'
          ? at(site.width / 2 - 2.5, -site.depth / 2 + t * site.depth)
          : at(-site.width / 2 + t * site.width, -site.depth / 2 + 2.5);
      if (Math.hypot(x - roadPoints[roadPoints.length - 1]![0], z - roadPoints[roadPoints.length - 1]![1]) > 5)
        tree(x, z, 0.75, k % 2 ? '#5f9b52' : '#4f8f48');
    }
  };
  AIRPORT_SITES.forEach(draw);
  const owned: { dispose: () => void }[] = [];
  const unit = new T.BoxGeometry(1, 1, 1);
  owned.push(unit);
  const place = new T.Object3D();
  for (const [color, boxes] of glowing) {
    const material = new T.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.2 });
    owned.push(material);
    const batch = new T.InstancedMesh(unit, material, boxes.length);
    boxes.forEach(([w, h, d, x, y, z], i) => {
      place.position.set(x, y, z);
      place.scale.set(w, h, d);
      place.updateMatrix();
      batch.setMatrixAt(i, place.matrix);
    });
    group.add(batch);
  }

  // the parked jets: one built in each livery and pressed into a few shapes (one per paint), copies of it at
  // every stand sharing them: fifteen jets cost what one does
  let parked: { airliner: T.Group; privateJet: T.Group; dispose: () => void } | null = null;
  const press = (livery: Livery) => {
    const jet = buildJet(livery, { detail: 'low' });
    jet.setGear(true);
    const holder = new T.Group();
    jet.group.position.y = JET_GROUND;
    holder.add(jet.group);
    holder.updateMatrixWorld(true);
    const byPaint = new Map<T.Material, T.BufferGeometry[]>();
    holder.traverse((object) => {
      if (!(object instanceof T.Mesh) || !object.visible) return;
      const shape = (object.geometry as T.BufferGeometry).clone();
      const flat = shape.index ? shape.toNonIndexed() : shape;
      if (flat !== shape) shape.dispose();
      for (const name of Object.keys(flat.attributes))
        if (!['position', 'normal', 'uv'].includes(name)) flat.deleteAttribute(name);
      if (!flat.attributes.uv)
        flat.setAttribute('uv', new T.Float32BufferAttribute(new Float32Array(flat.attributes.position!.count * 2), 2));
      flat.applyMatrix4(object.matrixWorld);
      // a mirrored part (the left wing) turns its faces inside out: put them the right way round again
      if (object.matrixWorld.determinant() < 0)
        for (const attribute of Object.values(flat.attributes) as T.BufferAttribute[])
          for (let i = 0; i + 2 < attribute.count; i += 3)
            for (let k = 0; k < attribute.itemSize; k += 1) {
              const second = attribute.getComponent(i + 1, k);
              attribute.setComponent(i + 1, k, attribute.getComponent(i + 2, k));
              attribute.setComponent(i + 2, k, second);
            }
      const material = object.material as T.Material;
      if (!byPaint.has(material)) byPaint.set(material, []);
      byPaint.get(material)!.push(flat);
    });
    const pressed = new T.Group();
    const shapes: T.BufferGeometry[] = [];
    for (const [material, parts] of byPaint) {
      const shape = mergeGeometries(parts);
      parts.forEach((part) => part.dispose());
      if (!shape) continue;
      shapes.push(shape);
      const mesh = new T.Mesh(shape, material);
      mesh.castShadow = true;
      pressed.add(mesh);
    }
    // the shapes are pressed; the jet's paint stays in use
    const keepPaint = jet.dispose;
    jet.group.traverse((object) => {
      if (object instanceof T.Mesh) (object.geometry as T.BufferGeometry).dispose();
    });
    return { group: pressed, dispose: () => (shapes.forEach((shape) => shape.dispose()), keepPaint()) };
  };
  const setLivery = (livery: Livery | null) => {
    for (const stand of stands) stand.holder.clear();
    parked?.dispose();
    parked = null;
    if (!livery) return;
    const airliner = press(livery);
    const privateJet = press(PRIVATE_LIVERY);
    parked = {
      airliner: airliner.group,
      privateJet: privateJet.group,
      dispose: () => {
        airliner.dispose();
        privateJet.dispose();
      },
    };
    for (const stand of stands) {
      const copy = (stand.airline ? airliner : privateJet).group.clone();
      copy.scale.setScalar(stand.scale);
      copy.position.y = 0.2;
      stand.holder.add(copy);
    }
  };

  return {
    streets,
    setLivery,
    dispose: () => {
      parked?.dispose();
      owned.forEach((item) => item.dispose());
    },
  };
}
